<template>
  <div class="enablebanking-ais loading-content">
    <div class="enablebanking-status text-center">
      <Spinner v-if="loading" class="enablebanking-status-spinner mx-auto" />
      <div class="enablebanking-status-info text-dark">{{ message }}</div>
      <div class="enablebanking-status-error text-danger">{{ error }}</div>
    </div>
    <auth-form :authOptions="authOptions" :error="error"
      v-if="authInputRequired && !loading && !authDataRequired && !bankIdAppUri" v-on:submit="submitCredentials" />
    <div v-if="!loading && !bankIdAppUri">
      <div v-if="authData.type == 'MESSAGE'">
        <p class="enablebanking-decouppled-text text-center"><strong>{{ translateBackendString(authData.value) }}</strong></p>
      </div>
      <div v-if="qrCodeUri" class="enablebanking-qrcode-container qrcode-container mb-4">
        <img id="qrCodeImage" class="enablebanking-qrcode-image qrcode-image" :src="qrCodeUri">
      </div>
      <div v-if="authData.type == 'OTP_CODE' && authDataRequired">
        <p class="enablebanking-decouppled-text text-center"><strong>{{ translateBackendString(authData.value) }}</strong></p>
        <form class="enablebanking-otp-form form-inline justify-content-center">
          <input type="text" name="otpCode" class="enablebanking-otp-input form-control" required :placeholder="$t('OTP code')" v-model="otpCode">
          <button class="enablebanking-otp-submit-button btn btn-theme" v-on:click="submitOtpCode">{{ $t('Submit') }}</button>
        </form>
      </div>
    </div>
    <div v-if="!loading && dataSharingConsentRequired">
      <div class="row justify-content-center mb-5">
        <div class="col-12 col-md-8 col-lg-6">
          <p class="text-justify">
            <i18n-t tag="span" keypath="Authentication is initiated by {0}." class="mr-1"><strong>{{ appName }}</strong>
            </i18n-t>
            <i18n-t tag="span"
              keypath="After you complete authentication, {0} will be shared with the authentication initiator."
              class="mr-1"><em>{{ $t(accessScope) }}</em></i18n-t>
            <i18n-t tag="span" keypath="By pressing {0} button you agree with the {1}." class="mr-1"><span>"{{ $t('Continue authentication') }}"</span><a :href="termsUrl" target="_blank">{{ isEnablebankingOrigin ? $t('terms of Enable Banking API service') : $t('terms of this service') }}</a></i18n-t>
            <i18n-t tag="span" keypath="Press {0} button if you are not willing to share your payment accounts data.">
              <span>"{{ $t('Cancel') }}"</span>
            </i18n-t>
          </p>
          <div class="form-inline justify-content-center">
            <button class="btn btn-theme" v-on:click="submitDataSharingConsent">{{ $t('Continue authentication')
              }}</button>
          </div>
          <div class="text-center">
            <button class="enablebanking-cancel-button btn btn-link" :disabled="cancelDisabled" v-on:click="cancelAuth">
              {{ $t("Cancel") }}
            </button>
          </div>
        </div>
      </div>
      <div class="row justify-content-center">
        <div class="col-12 col-md-8 col-lg-6 small text-muted">
          <p><strong>{{ $t('About {appName}', { appName: appName }) }}</strong></p>
          <p class="text-justify">{{ appDescriptionLocal }}</p>
          <about-service v-if="isEnablebankingOrigin" />
        </div>
      </div>
    </div>
    <bank-id v-if="!loading && bankIdAppUri" :bankIdAppUri="bankIdAppUri" :displayOnly="displayOnly"
      v-on:BankIdAppRedirect="openBankIdApp" v-on:BankIdResume="onBankIdResume" v-on:PageHidden="stopUpdate" />
    <div class="text-center" v-if="!loading && !dataSharingConsentRequired">
      <button class="enablebanking-cancel-button btn btn-link" :disabled="cancelDisabled" v-on:click="cancelAuth">
        {{ $t("Cancel") }}
      </button>
    </div>
  </div>
</template>
<script>
import config from '../config.js'
import AboutService from '../components/AboutService.vue'
import AuthForm from '../components/AuthForm.vue'
import BankId from '../components/BankId.vue'
import Spinner from '../components/elements/Spinner.vue'
import BaseService from '../services/BaseService.js'
import { hasTranslation, convertPlaceholdersToKeys, getDynamicValues } from '@/utils'
import { getBaseURL } from '../services/api.js'
import { closeNewTabChannel, receiveUrlFromNewTab } from '../services/newTabChannel.js'

const REQUEST_LIMIT = 300

export default {
  name: 'AIS',
  components: {
    AboutService,
    AuthForm,
    BankId,
    Spinner
  },
  props: {
    widgetEnabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      error: '',
      loading: true,
      cancelDisabled: false,
      message: '',
      prevStatus: null,
      status: null,
      country: null,
      bankName: '',
      business: false,
      authOptions: [],
      authData: '',
      authDataRequired: false,
      otpCode: '',
      appName: '',
      appDescription: '',
      accessScope: 'your payment accounts data',
      dataSharingConsentRequired: false,
      bankIdAppUri: '',
      displayOnly: null,
      qrCodeUri: '',
      timeoutId: 0,
      requestsCount: 0,
      lastVisibilitychange: new Date().getTime()
    }
  },
  computed: {
    apiOrigin () {
      const baseURL = getBaseURL()
      if (/^https?:\/\//i.test(baseURL)) { // remove trailing slash if there is https:// prefix
        return baseURL.replace(/\/$/, '')
      }
      return window.location.origin // fallback to current origin if baseURL is relative
    },
    isEnablebankingOrigin () {
      return config.enablebankingOrigins.includes(this.apiOrigin)
    },
    termsUrl () {
      if (this.isEnablebankingOrigin) return config.enablebankingTermsUrl
      return this.apiOrigin + '/terms' + (
        this.$i18n.locale ? `?locale=${this.$i18n.locale.toUpperCase()}` : ''
      )
    },
    authInputRequired: function () {
      return (this.authOptions.length > 1 || (
        this.authOptions.length === 1 &&
        this.authOptions[0].info &&
        this.authOptions[0].info.credentials &&
        this.authOptions[0].info.credentials.length
      )) || !!this.error
    },
    appDescriptionLocal: function () {
      if (typeof this.appDescription === 'string' && this.appDescription.length > 0) {
        return this.appDescription
      } else if (
        this.appDescription &&
        typeof this.appDescription[this.$i18n.locale] === 'string' &&
        this.appDescription[this.$i18n.locale].length > 0
      ) {
        return this.appDescription[this.$i18n.locale]
      } else {
        return this.$t('Application description is not provided.')
      }
    }
  },
  methods: {
    translateBackendString (backendStr) {
      return hasTranslation(this.$i18n, convertPlaceholdersToKeys(backendStr)) ? this.$t(convertPlaceholdersToKeys(backendStr), getDynamicValues(backendStr)) : backendStr
    },
    async handleWaitingUserDataSharingConsent (data) {
      this.appName = data.response.app_name
      this.appDescription = data.response.app_description
      this.dataSharingConsentRequired = true
      if (data.response.access) {
        if (data.response.access.balances && data.response.access.transactions) {
          this.accessScope = 'the list of your payment accounts, accounts balances and transactions'
        } else if (data.response.access.balances) {
          this.accessScope = 'the list of your payment accounts and accounts balances'
        } else if (data.response.access.transactions) {
          this.accessScope = 'the list of your payment accounts and accounts transactions'
        } else {
          this.accessScope = 'the list of your payment accounts'
        }
      }
      this.loading = false
    },
    async handleNotReady (data) {
      this.bankName = data.response.bank_name
      this.business = data.response.business
      this.authOptions = data.response.auth_info
      this.bankIdAppUri = ''
      this.qrCodeUri = ''
      if (data.error && data.error.message) {
        this.error = this.$t('Error during authentication:') + ' ' + this.$t(data.error.message)
      }
      if (this.authInputRequired) {
        this.loading = false
      } else {
        this.message = this.$t('Starting authorization')
        const r = await BaseService.startAuthorization(undefined, undefined, this.$i18n.locale)
        console.log(r)
        this.scheduleUpdate(0)
      }
    },
    async handleInvalid (data) {
      this.error = this.$t('Unable to authenticate')
      if (data.response.redirect_url) {
        if (!data.response.is_pis) {
          this.message = this.$t('Returning to the authentication initiator')
        }
        this.redirect(data.response.redirect_url, true)
      } else {
        this.message = this.$t('You need to close this window')
      }
    },
    async handleCancelled (data) {
      this.error = 'Authentication cancelled'
      if (data.response.redirect_url) {
        if (!data.response.is_pis) {
          this.message = this.$t('Returning to the authentication initiator')
        }
        this.redirect(data.response.redirect_url, true)
      } else {
        this.message = this.$t('You need to close this window')
      }
    },
    async handleBankUI (data) {
      this.message = ''
      try {
        await BaseService.continueAuthorizationWithRedirectUrl(window.location.href, this.$i18n.locale)
        // Get rid of query and fragment
        window.history.replaceState({}, '', '/ais/')
        this.scheduleUpdate(0) // success: schedule update immediately
      } catch (e) {
        this.error = this.$t('Unable to continue authorization')
        this.scheduleUpdate(500) // failure: let user see the message
      }
    },
    async handleReturnedFromBank (data) {
      // continue authorization is already started. Just wait
      this.scheduleUpdate(500)
    },
    async handleWaitingUserUIAuth (data) {
      this.message = this.$t('Redirecting to your account servicing provider')
      const r = await BaseService.notifyLeaving()
      if (r.data.response.message !== 'OK') {
        throw new Error(r.data.response.message)
      }
      this.redirect(data.response.redirect_url)
    },
    async handleWaitingUserDecoupledAuth (data) {
      this.message = ''
      if (data.response.auth_data) { // handle authData case (decoupled flow with message and embedded flow with OTP Code)
        this.authData = data.response.auth_data
        if (['OTP_CODE'].includes(this.authData.type)) {
          return this.handleBlockingAuthData(data) // embedded flow with OTP Code
        }
        return this.handleAuthData(data) // decoupled flow with message
      } else if (data.response.redirect_url) { // handle authHint case (QR code generation flow)
        this.loading = false
        this.bankIdAppUri = data.response.redirect_url
        const authHints = data.response.auth_url_hints
        if (authHints && authHints.length === 1) {
          this.displayOnly = authHints[0].auth_type
        }
        try {
          const r = await BaseService.continueAuthorization({ locale: this.$i18n.locale })
          console.log(r)
        } catch (e) {
          throw new Error(this.$t('Unable to continue decoupled authorization'))
        }
        this.scheduleUpdate(0, this.loading)
      } else { // handle no authData/authHint case (decoupled flow without message), for backward compatibility
        this.message = this.$t('Waiting for completion of authentication')
        try {
          const r = await BaseService.continueAuthorization({ locale: this.$i18n.locale })
          console.log(r)
        } catch (e) {
          throw new Error(this.$t('Unable to continue decoupled authorization'))
        }
        this.scheduleUpdate(500)
      }
    },
    handleBlockingAuthData (data) {
      this.authDataRequired = true
      this.otpCode = ''
      this.loading = false
      if (data.response.redirect_url) { // PUSH_OTP (see SDK issues #1277)
        const url = new URL(data.response.redirect_url)
        this.qrCodeUri = url.href
        this.$nextTick(() => {
          const qrCodeImage = document.getElementById('qrCodeImage')
          if (qrCodeImage === null) {
            // the element is not available
            return
          }
          qrCodeImage.style.display = 'block'
        })
      }
    },
    async handleAuthData (data) {
      this.authDataRequired = true
      this.loading = false
      this.bankIdAppUri = data.response.redirect_url
      this.displayOnly = this.authData.type
      try {
        const r = await BaseService.continueAuthorization({ locale: this.$i18n.locale })
        console.log(r)
      } catch (e) {
        throw new Error(this.$t('Unable to continue decoupled authorization'))
      }
      this.scheduleUpdate(1000, this.loading)
    },
    async handleAuthDone (data) {
      this.bankIdAppUri = ''
      if (!data.response.is_pis) {
        this.message = this.$t('Returning to the authentication initiator')
      } else {
        if (this.widgetEnabled) {
          this.$emit('redirect-pis', data)
          return
        }
      }
      this.redirect(data.response.redirect_url, true)
    },
    async handleAuthStarted (data) {
      // just wait
      this.scheduleUpdate(500)
    },
    listenForNewTabUrl () {
      if (!this.newTabChannel) {
        this.newTabChannel = receiveUrlFromNewTab((url) => { window.location.href = url })
      }
    },
    redirect (redirectUrl, replace = false) {
      this.loading = true
      this.stopUpdate()
      if (!this.widgetEnabled) {
        this.listenForNewTabUrl()
      }
      // In the widget the current history entry is the initiator's page, keep it.
      if (replace && !this.widgetEnabled) {
        window.location.replace(redirectUrl)
      } else {
        window.location.href = redirectUrl
      }
    },
    async updateStatus () {
      try {
        this.timeoutId = 0
        const r = await BaseService.getSessionStatus()
        this.error = ''
        this.prevStatus = this.status
        this.status = r.data.response.status
        // Checking country and updating locale if necessary
        if (r.data.response.country && r.data.response.country !== this.country) {
          this.country = r.data.response.country
          const urlLang = new URLSearchParams(window.location.search).get('locale')
          const selectedLang = window.localStorage.getItem('selected-language')
          if (config.countryLocales[this.country] &&
            config.countryLocales[this.country] !== this.$i18n.locale &&
            !(urlLang && this.$i18n.messages[urlLang]) &&
            !(selectedLang && this.$i18n.messages[selectedLang]) &&
            this.$i18n.messages[config.countryLocales[this.country]] &&
            (
              !r.data.response.language ||
              r.data.response.language.match(
                new RegExp(
                  // Checking if locale is in accepted languages (of it any are accepted)
                  `(^|[^\\w-])(${config.countryLocales[this.country]}|\\*)(\\W|$)`, 'i'
                )
              )
            )) {
            this.$root.$i18n.locale = config.countryLocales[this.country]
            window.document.documentElement.setAttribute(
              'lang',
              this.$root.$i18n.locale.toLocaleLowerCase()
            )
            window.localStorage.setItem('last-locale', this.$root.$i18n.locale)
          }
        }
        const handler = this[`handle${this.status}`]
        if (!handler) {
          this.message = ''
          throw new Error(
            this.$t(
              'Unable to handle authentication status "{status}"',
              { status: this.status }
            )
          )
        }
        handler(r.data)
      } catch (e) {
        if (e.message === 'Request aborted') { // disable retry if request was aborted by stopUpdate()
          return
        }
        let retry = true
        if (e.response) {
          if (e.response.status === 404) {
            this.error = this.$t('Unable to access server')
            retry = false
          } else if (e.response.status === 422 &&
            e.response.data &&
            e.response.data.detail &&
            e.response.data.detail.find(d =>
              d.loc[0] === 'cookie' &&
              d.loc[1] === 'sessionid' &&
              d.type === 'value_error.missing')
          ) {
            try {
              await BaseService.restoreSession(window.location.href)
            } catch (e) {
              this.error = this.$t('Authentication session has expired')
              retry = false
              try {
                const redirectKey = 'fallbackRedirectUri'
                const regex = new RegExp(`${redirectKey}="(?<${redirectKey}>.*)"`)
                const redirectUri = document.cookie.split(';').find(el => el.startsWith(redirectKey)).match(regex).groups[redirectKey]
                this.redirect(redirectUri, true)
              } catch (e) { }
            }
          } else {
            this.error = this.$t('Error while updating authentication status')
          }
        } else if (e.message) {
          this.error = e.message
        } else {
          this.error = this.$t('Unknown error occurred')
        }
        this.loading = retry
        if (retry && this.requestsCount < REQUEST_LIMIT) {
          this.scheduleUpdate(500)
        }
      } finally {
        this.requestsCount++
      }
    },
    scheduleUpdate (timeout, loading = true) {
      if (this.timeoutId !== 0) {
        // We don't want to schedule new update if there is already update scheduled
        return
      }
      this.loading = loading
      this.timeoutId = setTimeout(this.updateStatus, timeout)
    },
    async submitCredentials (data) {
      this.loading = true
      this.error = ''
      try {
        const r = await BaseService.startAuthorization(
          data.authMethod,
          Object.fromEntries(data.authCredentials.map(c => [c.name, c.value])),
          this.$i18n.locale
        )
        console.log(r)
      } catch (e) {
        console.error(e)
      }
      this.scheduleUpdate(0)
    },
    stopUpdate () {
      window.stop()
      clearTimeout(this.timeoutId)
      this.timeoutId = 0
    },
    openBankIdApp (data) {
      this.stopUpdate()
      if (!this.widgetEnabled) {
        this.listenForNewTabUrl()
      }
      if (data.blank) {
        window.open(data.redirectUri, '_blank')
      } else {
        window.location.href = data.redirectUri
      }
    },
    async onBankIdResume () {
      this.bankIdAppUri = ''
      this.loading = true
      await BaseService.continueAuthorization({ locale: this.$i18n.locale })
      this.scheduleUpdate(1000)
    },
    async submitOtpCode () {
      this.loading = true
      try {
        const r = await BaseService.continueAuthorizationWithOtpCode(this.otpCode, this.$i18n.locale)
        console.log(r)
      } catch (e) {
        console.error(e)
      }
      this.scheduleUpdate(0)
    },
    async submitDataSharingConsent () {
      this.loading = true
      try {
        const r = await BaseService.confirmDataSharingConsent()
        console.log(r)
      } catch (e) {
        console.error(e)
      }
      this.dataSharingConsentRequired = false
      this.scheduleUpdate(0)
    },
    isBackForwardNavigation () {
      const navigation = window.performance.getEntriesByType('navigation')
      if (navigation && navigation.length && navigation[0].type === 'back_forward') {
        return true
      }
      return false
    },
    async cancelAuth () {
      clearTimeout(this.timeoutId)
      this.timeoutId = 0
      window.stop()
      this.loading = true
      this.cancelDisabled = true
      this.error = ''
      try {
        const r = await BaseService.cancelAuth()
        console.log(r)
      } catch (e) {
        console.error(e)
      }
      this.scheduleUpdate(0)
    }
  },
  async mounted () {
    if (this.isBackForwardNavigation() && !this.widgetEnabled) {
      await this.cancelAuth()
    } else {
      this.scheduleUpdate(0)
    }
  },
  unmounted () {
    this.newTabChannel = closeNewTabChannel(this.newTabChannel)
  }
}
</script>

<style lang="scss" scoped>

.loading-content {
  padding-top: 20vh;
}

.qrcode-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.qrcode-image {
  border: 1px solid #ccc;
  display: none;
  width: 228px;
  height: 228px;
}
</style>
